import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { conversationsAPI, usersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { toast } from 'sonner';
import {
  MessageSquare,
  Send,
  Mail,
  AtSign,
  Loader2,
  Check,
  CheckCheck,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export default function EntityChat({ entityType, entityId, entityName }) {
  const { user } = useAuth();
  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  // Message input state
  const [newMessage, setNewMessage] = useState('');
  const [showMentions, setShowMentions] = useState(false);
  const [mentionSearch, setMentionSearch] = useState('');
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [sendAsEmail, setSendAsEmail] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);

  useEffect(() => {
    if (entityType && entityId) {
      fetchData();
    }
  }, [entityType, entityId]);

  useEffect(() => {
    if (expanded) {
      scrollToBottom();
    }
  }, [messages, expanded]);

  const fetchData = async () => {
    try {
      const [convRes, usersRes] = await Promise.all([
        conversationsAPI.getByEntity(entityType, entityId),
        usersAPI.getAll()
      ]);
      setConversation(convRes.data);
      setUsers(usersRes.data);
      
      // Fetch messages
      const messagesRes = await conversationsAPI.getMessages(convRes.data.id);
      setMessages(messagesRes.data);
    } catch (error) {
      console.error('Error fetching chat data:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !conversation) return;
    
    setSending(true);
    try {
      const res = await conversationsAPI.sendMessage(conversation.id, {
        content: newMessage,
        mentions: selectedMentions.map(u => u.id),
        send_as_email: sendAsEmail,
        email_to: sendAsEmail ? emailTo : null,
        email_subject: sendAsEmail ? emailSubject : null,
      });
      
      setMessages([...messages, res.data]);
      setNewMessage('');
      setSelectedMentions([]);
      setSendAsEmail(false);
      setEmailTo('');
      setEmailSubject('');
      
      if (sendAsEmail) {
        toast.success('Message et email envoyés');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleMentionClick = (mentionedUser) => {
    if (!selectedMentions.find(u => u.id === mentionedUser.id)) {
      setSelectedMentions([...selectedMentions, mentionedUser]);
    }
    setShowMentions(false);
    setMentionSearch('');
    messageInputRef.current?.focus();
  };

  const removeMention = (userId) => {
    setSelectedMentions(prev => prev.filter(u => u.id !== userId));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
    if (e.key === '@') {
      setShowMentions(true);
    }
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="border rounded-lg p-4">
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Chargement du chat...
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
      >
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-yellow-500" />
          <span className="font-medium">Discussion interne</span>
          <Badge variant="secondary" className="text-xs">
            {messages.length} message{messages.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        {expanded ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {expanded && (
        <>
          {/* Messages */}
          <div className="max-h-80 overflow-y-auto p-4 space-y-3 bg-white">
            {messages.length === 0 ? (
              <div className="text-center py-6 text-slate-400">
                <MessageSquare className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">Aucun message</p>
                <p className="text-xs">Commencez la discussion sur ce {entityType === 'devis' ? 'devis' : 'client'}</p>
              </div>
            ) : (
              messages.map((msg) => {
                const isOwnMessage = msg.sender_id === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={cn('flex gap-2', isOwnMessage && 'flex-row-reverse')}
                  >
                    <Avatar className="w-7 h-7 flex-shrink-0">
                      <AvatarFallback className={cn(
                        'text-[10px]',
                        isOwnMessage ? 'bg-yellow-100 text-yellow-700' : 'bg-slate-100 text-slate-700'
                      )}>
                        {getInitials(msg.sender_name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className={cn('max-w-[80%]', isOwnMessage && 'items-end')}>
                      <div className={cn('flex items-center gap-2 mb-0.5', isOwnMessage && 'flex-row-reverse')}>
                        <span className="text-[11px] font-medium text-slate-600">{msg.sender_name}</span>
                        <span className="text-[10px] text-slate-400">{formatTime(msg.created_at)}</span>
                        {msg.is_email && (
                          <Mail className="w-3 h-3 text-blue-500" title="Envoyé par email" />
                        )}
                      </div>
                      <div className={cn(
                        'rounded-lg px-3 py-2 text-sm',
                        isOwnMessage 
                          ? 'bg-yellow-500 text-white rounded-tr-sm' 
                          : 'bg-slate-100 text-slate-800 rounded-tl-sm'
                      )}>
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                        {msg.mentions?.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {msg.mentions.map(m => (
                              <span key={m.user_id} className={cn(
                                'text-[10px] px-1 rounded',
                                isOwnMessage ? 'bg-yellow-600' : 'bg-slate-200'
                              )}>
                                @{m.user_name}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 border-t bg-slate-50">
            {/* Mentions display */}
            {selectedMentions.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedMentions.map(u => (
                  <Badge 
                    key={u.id} 
                    variant="secondary" 
                    className="text-xs cursor-pointer hover:bg-red-100"
                    onClick={() => removeMention(u.id)}
                  >
                    @{u.name} ×
                  </Badge>
                ))}
              </div>
            )}
            
            {/* Email options */}
            {sendAsEmail && (
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Input
                  placeholder="Email du destinataire"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="h-8 text-sm"
                />
                <Input
                  placeholder="Sujet"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            )}
            
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Textarea
                  ref={messageInputRef}
                  placeholder="Écrivez un message... (@ pour mentionner)"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="min-h-[50px] resize-none text-sm pr-16"
                  rows={2}
                />
                <div className="absolute bottom-1.5 right-1.5 flex gap-0.5">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={cn('h-6 w-6 p-0', sendAsEmail && 'text-yellow-600 bg-yellow-50')}
                    onClick={() => setSendAsEmail(!sendAsEmail)}
                    title="Envoyer aussi par email"
                  >
                    <Mail className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0"
                    onClick={() => setShowMentions(!showMentions)}
                    title="Mentionner"
                  >
                    <AtSign className="w-3.5 h-3.5" />
                  </Button>
                </div>
                
                {/* Mentions dropdown */}
                {showMentions && (
                  <div className="absolute bottom-full left-0 mb-1 w-56 bg-white border rounded-lg shadow-lg max-h-40 overflow-y-auto z-10">
                    <Input
                      placeholder="Rechercher..."
                      value={mentionSearch}
                      onChange={(e) => setMentionSearch(e.target.value)}
                      className="border-0 border-b rounded-none h-8 text-sm"
                      autoFocus
                    />
                    {users
                      .filter(u => u.id !== user?.id)
                      .filter(u => !mentionSearch || u.name.toLowerCase().includes(mentionSearch.toLowerCase()))
                      .map(u => (
                        <button
                          key={u.id}
                          onClick={() => handleMentionClick(u)}
                          className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2 text-sm"
                        >
                          <Avatar className="w-5 h-5">
                            <AvatarFallback className="text-[8px]">{getInitials(u.name)}</AvatarFallback>
                          </Avatar>
                          <span>{u.name}</span>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>
              <Button
                onClick={handleSendMessage}
                disabled={!newMessage.trim() || sending}
                size="sm"
                className="h-[50px] w-[50px] bg-yellow-500 hover:bg-yellow-600"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
